import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { FLAGS } from './config/featureFlags';
import { migrateBrandKeys } from './utils/brandMigration';
import { hasAnalyticsConsent } from './hooks/useConsent';
import { initErrorTracking, reportError } from './services/errorTracking';
import { scrubSentryPayload } from './services/sentryRedaction';
import './index.css';
import './i18n'; // Initialize i18next before rendering
import { MotionConfig } from 'framer-motion';
import { AuthProvider } from './contexts/AuthContext';
import { FoundingMemberProvider } from './contexts/FoundingMemberContext';
import { initNativeShell } from './lib/native/bootstrap';
import { readStoredSwVersion, writeStoredSwVersion } from './utils/serviceWorkerVersion';
import App from './App.tsx';

// Migrate localStorage/cookies from old "imprint-*" keys to "vidi-*" (one-time, before React mounts)
migrateBrandKeys();

/**
 * Boot-time display-density init (blueprint §3): read the persisted choice
 * and stamp it on <html> at module parse, before first paint. Inlined here
 * — kept logic-identical to src/redesign/system/displayScale.ts (update
 * both) — because the main bundle must not eagerly import the Design-shared
 * chunk. (Moved from the deleted DesignV2Context in the flag flip, bd
 * Vidi-tui.)
 */
function initDisplayScaleAtBoot(): void {
  let scale = 'auto';
  try {
    const stored = localStorage.getItem('vidi-display-scale');
    if (stored === 'comfort' || stored === 'standard') scale = stored;
  } catch {
    // storage unavailable (private mode) — fall back to auto
  }
  document.documentElement.dataset.displayScale = scale;
}
initDisplayScaleAtBoot();

// ─── Aggressive service worker update ────────────────────────────
// Ensures users never get stuck on a stale cached version.
// 1. If a new SW is waiting, tell it to skipWaiting and reload.
// 2. If the cached version doesn't match the deployed version, nuke the SW.
declare const __APP_VERSION__: string;

// Detect OAuth callback — tokens may be in the hash (#access_token=...) for implicit flow
// or in query params (?code=...) for PKCE flow. Any reload would lose them.
const isOAuthCallback =
  window.location.hash.includes('access_token') ||
  window.location.hash.includes('refresh_token') ||
  window.location.search.includes('code=') ||
  window.location.hash.includes('error_description');

// VitePWA is disabled for native builds (vite.config.ts), so registerSW.js
// is never injected there. Also skip on native: this block's four
// window.location.reload() calls would be a reload loop inside the app,
// which for a user is an uninstall.
if (!FLAGS.nativeIOS && 'serviceWorker' in navigator && !isOAuthCallback) {
  const lastSwVersion = readStoredSwVersion();
  const currentVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '';

  if (currentVersion && lastSwVersion && lastSwVersion !== currentVersion) {
    // Version mismatch — nuke stale SW.
    // void + .catch: SW APIs can reject (storage quota, security context).
    // Silent rejection would leave users stuck on the stale shell with no signal.
    void navigator.serviceWorker
      .getRegistrations()
      .then((regs) => {
        regs.forEach((reg) => reg.unregister());
        // Reload only if the new marker was saved; otherwise the old marker
        // would mismatch again on every load and loop.
        if (writeStoredSwVersion(currentVersion)) window.location.reload();
      })
      .catch((err) => {
        reportError(err, { operation: 'sw.unregisterStale' });
      });
  } else {
    if (currentVersion) writeStoredSwVersion(currentVersion);

    void navigator.serviceWorker
      .getRegistration()
      .then((reg) => {
        if (!reg) return;

        // If a new SW is already waiting, activate it immediately
        if (reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          window.location.reload();
          return;
        }

        // Listen for future updates
        reg.addEventListener('updatefound', () => {
          const newSW = reg.installing;
          if (!newSW) return;
          newSW.addEventListener('statechange', () => {
            if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
              newSW.postMessage({ type: 'SKIP_WAITING' });
              window.location.reload();
            }
          });
        });
      })
      .catch((err) => {
        reportError(err, { operation: 'sw.getRegistration' });
      });

    // Listen for controller change (new SW took over) — reload to get fresh assets
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });
  }
} else if (!FLAGS.nativeIOS && 'serviceWorker' in navigator && isOAuthCallback) {
  // Still update the stored version during OAuth callback (no reload)
  const currentVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '';
  if (currentVersion) writeStoredSwVersion(currentVersion);
}

// Initialize Sentry for error tracking — gated behind analytics consent (TDDDG Section 25).
// If the user later accepts analytics, Sentry will initialize on next page load.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn && hasAnalyticsConsent()) {
  void initErrorTracking({
    dsn: sentryDsn,
    environment: FLAGS.nativeIOS ? 'ios' : import.meta.env.MODE,
    enabled: true,
    sampleRate: 1.0,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    ignoreErrors: ['ResizeObserver loop', 'Non-Error promise rejection'],
    // The email confirmation link carries live tokens in the URL; never let them leave the page.
    beforeSend: scrubSentryPayload,
    beforeBreadcrumb: scrubSentryPayload,
    beforeSendTransaction: scrubSentryPayload,
  });
}

// Status bar, keyboard and resume-handler wiring (plan §3.1/3.4/3.6, bead
// Vidi-d25.4). Dead code on web: FLAGS.nativeIOS is the literal `false`
// there, so Rollup deletes this whole call (N1).
if (FLAGS.nativeIOS) void initNativeShell();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MotionConfig reducedMotion="user">
      <AuthProvider>
        <FoundingMemberProvider>
          <App />
        </FoundingMemberProvider>
      </AuthProvider>
    </MotionConfig>
  </StrictMode>,
);
